import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import ReactPlayer from 'react-player';
import { useMediaQuery } from 'react-responsive';
import {
  BottomControlContainer,
  ButtonControlBar,
  ButtonControlsSection,
  Container,
  ControlOverlay,
  ErrorMessage,
  IconButton,
  MidControlContainer,
  PlayPauseButton,
  ProgressBarContainer,
  SeekButton,
  SliderContainer,
  SpeedControl,
  VolumeContainer,
  VolumeSlider,
} from './styles';
import normalizeVideoUrl from '../../utils/normalizeVideoUrl';
import {
  DefaultScreenIcon,
  FowardVideoIcon,
  FullscreenIcon,
  PauseVideoIcon,
  PlayVideoIcon,
  RewindVideoIcon,
  SoundMuteVideoIcon,
  SoundVideoIcon,
  TheaterModeIcon,
} from '../../assets/svg/SVGComponents';

interface IVideoPlayerProps {
  url?: string; // URL do vídeo (bucket da AWS ou YouTube)
  onEnded?: () => void;
  /** Modo teatro ativo. Só habilita o botão quando onToggleTheater é passado. */
  theater?: boolean;
  onToggleTheater?: () => void;
}

interface IVideoState {
  playing: boolean;
  muted: boolean;
  volume: number;
  playbackRate: number;
  speed: number;
  played: number;
  seeking: boolean;
  buffer: boolean;
}

const VideoPlayer: React.FC<IVideoPlayerProps> = ({
  url,
  onEnded,
  theater = false,
  onToggleTheater,
}) => {
  const playerRef = useRef<ReactPlayer>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHoveredMid, setIsHoveredMid] = useState(false);
  const [isHoveredBottom, setIsHoveredBottom] = useState(false);
  const [started, setStarted] = useState(false);
  const [videoState, setVideoState] = useState<IVideoState>({
    playing: false,
    muted: false,
    volume: 0.5,
    playbackRate: 1.0,
    speed: 1.0,
    played: 0,
    seeking: false,
    buffer: true,
  });
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });
  const isPortrait = useMediaQuery({ query: '(orientation: portrait)' });
  const [isMobile, setIsMobile] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLive, setIsLive] = useState(false);

  // Normaliza a URL antes de entregar ao react-player: links do YouTube em
  // formatos variados (youtu.be, /shorts, /embed, iframe colado) viram a forma
  // canônica; qualquer outra URL passa intacta.
  const playerUrl = useMemo(() => normalizeVideoUrl(url), [url]);

  // URL inválida (nenhum player reconhece) faria o react-player renderizar um
  // <video> mudo. URL ausente não é erro: o conteúdo ainda pode estar chegando.
  const isInvalidUrl = useMemo(
    () => !!playerUrl && !ReactPlayer.canPlay(playerUrl),
    [playerUrl],
  );

  // Só tratamos como falha definitiva se o player nunca chegou a ficar pronto —
  // o onError do react-player também dispara em situações transitórias.
  const isReadyRef = useRef(false);

  useEffect(() => {
    isReadyRef.current = false;
    setHasError(false);
  }, [playerUrl]);

  const handleError = useCallback(
    (error: unknown): void => {
      // eslint-disable-next-line no-console
      console.error('[VideoPlayer] falha ao carregar o vídeo', playerUrl, error);
      if (!isReadyRef.current) setHasError(true);
    },
    [playerUrl],
  );

  // Controle de visibilidade dos controles para mobile
  const [controlsVisible, setControlsVisible] = useState(true);

  // Referência para o timeout que esconde os controles
  const hideControlsTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(() => {
    if (isTabletOrMobile || isPortrait) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [isPortrait, isTabletOrMobile]);

  const handleVideoEnded = (): void => {
    if (onEnded) {
      onEnded();
    }
  };

  // Função para lidar com toques em dispositivos mobile para mostrar/ocultar os controles
  const handleTouch = (e: React.TouchEvent): void => {
    const target = e.target as HTMLElement;
    if (target.closest('.control-element')) return;

    // Lógica original de alternar controles
    if (controlsVisible) {
      setControlsVisible(false);
      if (hideControlsTimeout.current)
        clearTimeout(hideControlsTimeout.current);
    } else {
      setControlsVisible(true);
      resetHideControlsTimeout();
    }
  };

  const resetHideControlsTimeout = (): void => {
    if (hideControlsTimeout.current) clearTimeout(hideControlsTimeout.current);
    hideControlsTimeout.current = setTimeout(() => {
      setControlsVisible(false);
    }, 3000);
  };

  // Desestruturação das propriedades do estado
  const { playing, muted, volume, playbackRate } = videoState;

  // Obtendo o tempo atual e a duração
  const currentTime: number = playerRef.current
    ? playerRef.current.getCurrentTime()
    : 0;
  const duration: number = playerRef.current
    ? playerRef.current.getDuration()
    : 0;

  // Função para formatação do tempo (exemplo: mm:ss)
  const formatTime = (time: number): string => {
    if (!Number.isFinite(time) || time < 0) return '00:00';

    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;
    return `${formattedMinutes}:${formattedSeconds}`;
  };

  const formattedCurrentTime = formatTime(currentTime);
  const formattedDuration = formatTime(duration);

  // Alterna entre play e pause
  const playPauseHandler = (): void => {
    setVideoState({ ...videoState, playing: !videoState.playing });
    setStarted(true);
  };

  const handlePlaybabckrate = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    setVideoState({ ...videoState, playbackRate: parseFloat(e.target.value) });
  };

  // Retrocede 5 segundos no vídeo.
  // A unidade 'seconds' é obrigatória: sem ela o react-player interpreta
  // qualquer valor entre 0 e 1 como fração da duração total.
  const rewindHandler = (): void => {
    if (playerRef.current) {
      const target = Math.max(0, playerRef.current.getCurrentTime() - 5);
      playerRef.current.seekTo(target, 'seconds');
    }
  };

  // Avança 10 segundos no vídeo
  const handleFastFoward = (): void => {
    if (playerRef.current) {
      const total = playerRef.current.getDuration() || 0;
      const target = playerRef.current.getCurrentTime() + 10;
      playerRef.current.seekTo(
        total > 0 ? Math.min(target, total) : target,
        'seconds',
      );
    }
  };

  // Fullscreen no container inteiro (funciona igual para <video> e iframe)
  const handleFullscreen = useCallback((): void => {
    const element = containerRef.current;
    if (!element) return;

    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => undefined);
    } else {
      element.requestFullscreen().catch(() => undefined);
    }
  }, []);

  // Handler para atualizar o progresso e controlar a visibilidade dos controles
  const progressHandler = (state: Partial<IVideoState>): void => {
    if (!videoState.seeking) {
      setVideoState({ ...videoState, ...state });
    }
  };

  // Atualiza o progresso conforme a interação do usuário
  const seekHandler = (e: MouseEvent | ChangeEvent, value: number): void => {
    const newPlayed = parseFloat((value / 100).toString());
    setVideoState({ ...videoState, played: newPlayed });
    if (playerRef.current) {
      playerRef.current.seekTo(newPlayed);
    }
  };

  // Handler para quando o usuário solta o controle de seek
  const seekMouseUpHandler = (
    e: React.MouseEvent<HTMLInputElement> | React.ChangeEvent<HTMLInputElement>,
    value: number,
  ): void => {
    setVideoState({ ...videoState, seeking: false });
    if (playerRef.current) {
      playerRef.current.seekTo(value / 100);
    }
  };

  const volumeChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const value = Number(e.target.value);
    const newVolume = value; // Supondo que o range de volume seja de 0 a 1
    setVideoState(prev => ({
      ...prev,
      volume: newVolume,
      muted: newVolume === 0,
    }));
  };

  // Handler para quando o usuário solta o controle de volume
  const volumeSeekUpHandler = (e: React.MouseEvent<HTMLInputElement>): void => {
    const value = Number((e.target as HTMLInputElement).value);
    const newVolume = value; // Supondo que o range de volume seja de 0 a 1
    setVideoState(prev => ({
      ...prev,
      volume: newVolume,
      muted: newVolume === 0,
    }));
  };

  // Alterna o mudo
  const muteHandler = (): void => {
    setVideoState({ ...videoState, muted: !videoState.muted });
  };

  // Handler chamado quando o usuário começa a arrastar o controle de seek
  const onSeekMouseDownHandler = (): void => {
    setVideoState({ ...videoState, seeking: true });
  };

  // Exibe os controles quando o mouse se move

  // Handlers para início e fim de buffering
  const bufferStartHandler = (): void => {
    setVideoState({ ...videoState, buffer: true });
  };

  const bufferEndHandler = (): void => {
    setVideoState({ ...videoState, buffer: false });
  };

  // Sem URL ainda: container vazio, sem mensagem de erro.
  if (!playerUrl) {
    return <Container ref={containerRef} />;
  }

  if (isInvalidUrl || hasError) {
    return (
      <Container ref={containerRef}>
        <ErrorMessage>
          Não foi possível carregar este vídeo. Verifique o link do conteúdo.
        </ErrorMessage>
      </Container>
    );
  }

  return (
    <Container ref={containerRef} onTouchStart={handleTouch}>
      <ReactPlayer
        ref={playerRef}
        className="react-player"
        url={playerUrl}
        width="100%"
        height="100%"
        playing={playing}
        playbackRate={playbackRate}
        volume={volume}
        muted={muted}
        playsinline
        onProgress={progressHandler}
        onBuffer={bufferStartHandler}
        onBufferEnd={bufferEndHandler}
        onEnded={handleVideoEnded}
        onReady={() => {
          isReadyRef.current = true;
        }}
        onError={handleError}
        onDuration={total => setIsLive(!total || !Number.isFinite(total))}
        config={{
          file: {
            attributes: {
              controlsList: 'nodownload',
            },
          },
          youtube: {
            // `origin` já é definido pelo próprio react-player
            playerVars: {
              rel: 0,
              modestbranding: 1,
              iv_load_policy: 3,
              playsinline: 1,
            },
          },
        }}
      />
      {/* Barra de controle customizada */}
      {(!isMobile || controlsVisible) && (
        <ControlOverlay className="controls-overlay">
          <MidControlContainer
            $isPaused={!videoState.playing}
            $isHovered={isHoveredMid}
            onMouseEnter={() => setIsHoveredMid(true)}
            onMouseLeave={() => setIsHoveredMid(false)}
          >
            <SeekButton
              className="control-element"
              type="button"
              onClick={rewindHandler}
            >
              <RewindVideoIcon width="2rem" height="2rem" fill="#fff" />
            </SeekButton>
            <PlayPauseButton
              className="control-element"
              $isPlaying={videoState.playing}
              type="button"
              onClick={() => playPauseHandler()}
            >
              {!started || !videoState.playing ? (
                <PlayVideoIcon width="2rem" height="2rem" fill="#fff" />
              ) : (
                <PauseVideoIcon width="2rem" height="2rem" fill="#fff" />
              )}
            </PlayPauseButton>
            <SeekButton
              className="control-element"
              type="button"
              onClick={() => handleFastFoward()}
            >
              <FowardVideoIcon width="2rem" height="2rem" fill="#fff" />
            </SeekButton>
          </MidControlContainer>

          <BottomControlContainer
            $isPaused={!videoState.playing}
            $isHovered={isHoveredBottom || isHoveredMid}
            onMouseEnter={() => setIsHoveredBottom(true)}
            onMouseLeave={() => setIsHoveredBottom(false)}
          >
            {!isLive && (
              <ProgressBarContainer
                value={videoState.played * 100}
                onChange={e => seekHandler(e, e.target.valueAsNumber)}
                onMouseDown={() => onSeekMouseDownHandler()}
                onMouseUp={e =>
                  seekMouseUpHandler(
                    e,
                    (e.target as HTMLInputElement).valueAsNumber,
                  )
                }
              />
            )}

            <ButtonControlsSection>
              <div>
                <ButtonControlBar
                  className="control-element"
                  onClick={() => playPauseHandler()}
                >
                  {!started || !videoState.playing ? (
                    <PlayVideoIcon
                      width="1.2rem"
                      height="1.2rem"
                      fill="#1e88e5"
                    />
                  ) : (
                    <PauseVideoIcon
                      width="1.2rem"
                      height="1.2rem"
                      fill="#1e88e5"
                    />
                  )}
                </ButtonControlBar>

                <span>
                  {isLive
                    ? 'AO VIVO'
                    : `${formattedCurrentTime} / ${formattedDuration}`}
                </span>
                <VolumeContainer className="control-element">
                  <button type="button" onClick={muteHandler}>
                    {videoState.volume > 0 && !videoState.muted ? (
                      <SoundVideoIcon
                        width="1.2rem"
                        height="1.2rem"
                        fill="#1e88e5"
                      />
                    ) : (
                      <SoundMuteVideoIcon
                        width="1.2rem"
                        height="1.2rem"
                        fill="#1e88e5"
                      />
                    )}
                  </button>

                  <VolumeSlider
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={videoState.volume}
                    onChange={e => volumeChangeHandler(e)}
                    onMouseUp={e => volumeSeekUpHandler(e)}
                  />
                </VolumeContainer>
              </div>
              <div>
                {/* Transmissões ao vivo não têm duração nem controle de velocidade */}
                {!isLive && (
                  <SpeedControl
                    className="control-element"
                    value={videoState.playbackRate}
                    onChange={e => handlePlaybabckrate(e)}
                  >
                    <option value="0.5">0.5x</option>
                    <option value="1">1x</option>
                    <option value="1.5">1.5x</option>
                    <option value="2">2x</option>
                  </SpeedControl>
                )}
                {/* Modo teatro não faz sentido no mobile: a largura já é total */}
                {!!onToggleTheater && !isMobile && (
                  <IconButton
                    className="control-element"
                    type="button"
                    onClick={onToggleTheater}
                    title={theater ? 'Modo padrão' : 'Modo teatro'}
                    aria-label={theater ? 'Modo padrão' : 'Modo teatro'}
                  >
                    {theater ? (
                      <DefaultScreenIcon
                        width="1.6rem"
                        height="1.6rem"
                        stroke="#1e88e5"
                      />
                    ) : (
                      <TheaterModeIcon
                        width="1.6rem"
                        height="1.6rem"
                        stroke="#1e88e5"
                      />
                    )}
                  </IconButton>
                )}
                <IconButton
                  className="control-element"
                  type="button"
                  onClick={handleFullscreen}
                  title="Tela cheia"
                  aria-label="Tela cheia"
                >
                  <FullscreenIcon width="2rem" height="2rem" stroke="#1e88e5" />
                </IconButton>
              </div>
            </ButtonControlsSection>

            <SliderContainer />
          </BottomControlContainer>
        </ControlOverlay>
      )}
      {/* Botão de controle no canto inferior direito */}
    </Container>
  );
};

export default VideoPlayer;
